import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  SignIn,
  useAuth,
  useClerk,
} from '@clerk/clerk-react';
import { useTripStore } from './store/tripStore';
import { useTripsListStore } from './store/tripsListStore';
import { useLibraryStore } from './store/libraryStore';
import { useUserStore } from './store/userStore';
import { useAuthStore, syncAuthState } from './store/authStore';
import { useInstanceSettingsStore } from './store/instanceSettingsStore';
import { KanbanBoard } from './components/KanbanBoard';
import { TripsPage } from './pages/TripsPage';
import { SettingsPage } from './pages/SettingsPage';
import { LibraryPage } from './pages/LibraryPage';
import { TagsPage } from './pages/TagsPage';
import { InvitePage } from './pages/InvitePage';
import { CollectionsPage } from './pages/CollectionsPage';
import { CollectionInvitePage } from './pages/CollectionInvitePage';
import { useCollectionsStore } from './store/collectionsStore';
import { UserIdentityModal } from './components/UserIdentityModal';
import { setClerkInstance } from './services/api';
import { setClerkInstanceForTokens } from './services/apiTokenService';

// Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable');
}

// Loading screen component
function LoadingScreen({ message = 'Loading...' }: { message?: string }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-kanban-bg">
      <div className="flex flex-col items-center gap-4">
        <svg
          width="32"
          height="32"
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="animate-pulse"
        >
          <path
            d="M44 11.2727C44 14.0109 39.8386 16.3957 33.69 17.6364C39.8386 18.877 44 21.2618 44 24C44 26.7382 39.8386 29.123 33.69 30.3636C39.8386 31.6043 44 33.9891 44 36.7273C44 40.7439 35.0457 44 24 44C12.9543 44 4 40.7439 4 36.7273C4 33.9891 8.16144 31.6043 14.31 30.3636C8.16144 29.123 4 26.7382 4 24C4 21.2618 8.16144 18.877 14.31 17.6364C8.16144 16.3957 4 14.0109 4 11.2727C4 7.25611 12.9543 4 24 4C35.0457 4 44 7.25611 44 11.2727Z"
            fill="#111418"
          />
        </svg>
        <span className="text-sm text-text-secondary font-medium">
          {message}
        </span>
      </div>
    </div>
  );
}

// Error screen component
function ErrorScreen({ error, onRetry }: { error: string; onRetry: () => void }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-kanban-bg">
      <div className="bg-white rounded-2xl p-10 max-w-[400px] text-center border border-border">
        <span className="material-symbols-outlined text-[40px] text-red-500 mb-4 block">
          error
        </span>
        <h2 className="text-lg font-bold mb-2">Something went wrong</h2>
        <p className="text-sm text-text-secondary mb-6">{error}</p>
        <button
          onClick={onRetry}
          className="px-6 py-2.5 bg-text-primary text-white border-none rounded-lg text-sm font-bold cursor-pointer font-sans"
        >
          Retry
        </button>
      </div>
    </div>
  );
}

// Sync Clerk auth state to our store and API
function ClerkAuthSync() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const clerk = useClerk();

  useEffect(() => {
    const email = clerk.user?.primaryEmailAddress?.emailAddress;
    syncAuthState(isLoaded, isSignedIn ?? false, userId ?? null, email);
  }, [isLoaded, isSignedIn, userId, clerk.user]);

  useEffect(() => {
    if (isLoaded && clerk) {
      setClerkInstance(clerk);
      setClerkInstanceForTokens(clerk);
    }
  }, [isLoaded, clerk]);

  return null;
}

// Trip board route - loads specific trip
function TripBoardRoute() {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { trip, loading, error, loadTrip, unloadTrip } = useTripStore();
  const { setActiveTripId } = useTripsListStore();

  useEffect(() => {
    if (tripId) {
      setActiveTripId(tripId);
      loadTrip(tripId).then((found) => {
        if (!found) {
          navigate('/');
        }
      });
    }

    return () => {
      unloadTrip();
      setActiveTripId(null);
    };
  }, [tripId, loadTrip, unloadTrip, setActiveTripId, navigate]);

  if (loading || !trip) {
    return <LoadingScreen message="Loading trip..." />;
  }

  if (error) {
    return (
      <ErrorScreen
        error={error}
        onRetry={() => tripId && loadTrip(tripId)}
      />
    );
  }

  return <KanbanBoard />;
}

// App wrapper that handles initial loading
function AppContent() {
  const location = useLocation();
  const { isLoaded, isSignedIn, userId } = useAuth();
  const { loading: tripsLoading, error: tripsError, loadTrips } = useTripsListStore();
  const { loading: libraryLoading, error: libraryError, loadLibrary } = useLibraryStore();
  const { loading: settingsLoading, loadSettings } = useInstanceSettingsStore();
  const { loadCollections } = useCollectionsStore();
  const { displayName, setDisplayName } = useUserStore();
  const [initialized, setInitialized] = useState(false);

  // Check if we're on special routes (need handling before auth check)
  const isInviteRoute = location.pathname.startsWith('/invite/') && !location.pathname.startsWith('/invite/collection/');
  const isCollectionInviteRoute = location.pathname.startsWith('/invite/collection/');

  // Load data once authenticated
  useEffect(() => {
    if (isLoaded && isSignedIn && !initialized) {
      const init = async () => {
        await Promise.all([loadLibrary(), loadTrips(), loadSettings(), loadCollections()]);
        setInitialized(true);
      };
      init();
    }
  }, [isLoaded, isSignedIn, loadLibrary, loadTrips, loadSettings, loadCollections, initialized]);

  // Invite routes - render with special handling
  if (isInviteRoute) {
    return (
      <Routes>
        <Route path="/invite/:token" element={<InvitePage />} />
      </Routes>
    );
  }

  if (isCollectionInviteRoute) {
    return (
      <Routes>
        <Route path="/invite/collection/:token" element={<CollectionInvitePage />} />
      </Routes>
    );
  }

  // Wait for Clerk to load
  if (!isLoaded) {
    return <LoadingScreen message="Checking authentication..." />;
  }

  // Load data
  const loading = !initialized || tripsLoading || libraryLoading || settingsLoading;
  const error = tripsError || libraryError;

  if (loading) {
    return <LoadingScreen message="Loading POI Planner..." />;
  }

  if (error) {
    return (
      <ErrorScreen
        error={error}
        onRetry={() => {
          setInitialized(false);
          Promise.all([loadLibrary(), loadTrips(), loadSettings(), loadCollections()]).then(() => setInitialized(true));
        }}
      />
    );
  }

  return (
    <>
      {!displayName && <UserIdentityModal onSubmit={setDisplayName} />}
      <Routes>
        <Route path="/" element={<TripsPage />} />
        <Route path="/trips/:tripId" element={<TripBoardRoute />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/collections" element={<CollectionsPage />} />
        <Route path="/tags" element={<TagsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Routes>
    </>
  );
}

// Login page using Clerk's SignIn component
function LoginPage() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-kanban-bg">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">Welcome to POI Planner</h1>
          <p className="text-text-secondary">Sign in to start planning your trips</p>
        </div>
        <SignIn
          appearance={{
            elements: {
              rootBox: 'mx-auto',
              card: 'shadow-lg',
            },
          }}
        />
      </div>
    </div>
  );
}

export function App() {
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY}>
      <BrowserRouter>
        <ClerkAuthSync />
        <SignedOut>
          <LoginPage />
        </SignedOut>
        <SignedIn>
          <AppContent />
        </SignedIn>
      </BrowserRouter>
    </ClerkProvider>
  );
}
